import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Shield, LogIn, Pencil, PlusCircle, ChevronLeft, ChevronRight, Search,
  Layers, Clock, User as UserIcon, X, Download, Archive, FileText, CheckCircle2,
  AlertTriangle, XCircle, MinusCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryConfig = {
  auth: { label: 'Logins', icon: LogIn, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  data_change: { label: 'Edits', icon: Pencil, color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  activity: { label: 'Additions', icon: PlusCircle, color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
};

const actionColors = {
  login: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  logout: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  create: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  update: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  delete: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  export: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  archive_clear: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
};

const ROLES = ['Admin', 'Staff', 'Player', 'Parent'];

const COVERAGE_STATUS_META = {
  complete:             { label: 'Complete',                icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  complete_no_activity: { label: 'Hooked, no activity yet', icon: CheckCircle2, color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  partial:              { label: 'Partial',                 icon: AlertTriangle, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  missing:              { label: 'Missing',                 icon: XCircle, color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  not_applicable:       { label: 'Not applicable',          icon: MinusCircle, color: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
};

const formatValue = (v) => {
  if (v === null || v === undefined) return <span className="text-gray-400 italic">—</span>;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') {
    if (!v.trim()) return <span className="text-gray-400 italic">(empty)</span>;
    return v.length > 140 ? v.slice(0, 140) + '…' : v;
  }
  try { const j = JSON.stringify(v); return j.length > 140 ? j.slice(0, 140) + '…' : j; }
  catch { return String(v); }
};

const formatTime = (ts) => new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const formatTimeFull = (ts) => new Date(ts).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium' });

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const SystemLogs = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  const [tab, setTab] = useState('active');

  // ─── Active Logs state ───────────────────────────────────────
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [logUsers, setLogUsers] = useState([]);
  const [logModules, setLogModules] = useState([]);
  const [logActions, setLogActions] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [filterCategory, setFilterCategory] = useState('');
  const [filterModule, setFilterModule] = useState(searchParams.get('module') || '');
  const [filterAction, setFilterAction] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterTargetId, setFilterTargetId] = useState(searchParams.get('target_id') || '');

  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ─── Archives ─────────────────────────────────────────────────
  const [archives, setArchives] = useState([]);
  const [archivesLoading, setArchivesLoading] = useState(false);
  const [selectedArchive, setSelectedArchive] = useState(null);

  // ─── Coverage ────────────────────────────────────────────────
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);

  // ─── Archive/Clear modal ─────────────────────────────────────
  const [archiveModal, setArchiveModal] = useState(false);
  const [archiveScope, setArchiveScope] = useState('all');
  const [archiveBeforeDate, setArchiveBeforeDate] = useState('');
  const [archiveBusy, setArchiveBusy] = useState(false);

  const currentFilters = () => ({
    category: filterCategory || undefined,
    module: filterModule || undefined,
    action: filterAction || undefined,
    user_id: filterUser || undefined,
    user_role: filterRole || undefined,
    user_email: filterEmail || undefined,
    target_id: filterTargetId || undefined,
    date_from: filterDateFrom ? new Date(filterDateFrom).toISOString() : undefined,
    date_to: filterDateTo ? new Date(filterDateTo + 'T23:59:59').toISOString() : undefined,
    search: searchText || undefined,
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize), archived: 'false' });
      if (filterCategory) params.append('category', filterCategory);
      if (filterModule) params.append('module', filterModule);
      if (filterAction) params.append('action', filterAction);
      if (filterUser) params.append('user_id', filterUser);
      if (filterRole) params.append('user_role', filterRole);
      if (filterEmail) params.append('user_email', filterEmail);
      if (filterDateFrom) params.append('date_from', new Date(filterDateFrom).toISOString());
      if (filterDateTo) params.append('date_to', new Date(filterDateTo + 'T23:59:59').toISOString());
      if (searchText) params.append('search', searchText);
      if (filterTargetId) params.append('target_id', filterTargetId);
      const res = await axios.get(`${API}/logs?${params.toString()}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch { toast.error('Failed to fetch logs'); }
    finally { setLoading(false); }
  }, [page, pageSize, filterCategory, filterModule, filterAction, filterUser, filterRole, filterEmail, filterDateFrom, filterDateTo, searchText, filterTargetId]);

  const fetchMeta = async () => {
    try {
      const [statsRes, usersRes, modulesRes, actionsRes] = await Promise.all([
        axios.get(`${API}/logs/stats`),
        axios.get(`${API}/logs/users`),
        axios.get(`${API}/logs/modules`),
        axios.get(`${API}/logs/actions`).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data); setLogUsers(usersRes.data);
      setLogModules(modulesRes.data); setLogActions(actionsRes.data || []);
    } catch (err) {
      // Non-critical meta (stats/users/modules/actions) — log but don't disrupt UX.
      console.warn('Failed to fetch System Logs metadata:', err);
    }
  };

  const fetchArchives = useCallback(async () => {
    if (!isAdmin) return;
    setArchivesLoading(true);
    try {
      const res = await axios.get(`${API}/logs/archives`);
      setArchives(res.data.archives || []);
    } catch { toast.error('Failed to load archives'); }
    finally { setArchivesLoading(false); }
  }, [isAdmin]);

  const fetchCoverage = useCallback(async () => {
    if (!isAdmin) return;
    setCoverageLoading(true);
    try {
      const res = await axios.get(`${API}/logs/coverage`);
      setCoverage(res.data);
    } catch { toast.error('Failed to load audit coverage'); }
    finally { setCoverageLoading(false); }
  }, [isAdmin]);

  useEffect(() => { fetchMeta(); }, []);
  useEffect(() => { if (tab === 'active') fetchLogs(); }, [fetchLogs, tab]);
  useEffect(() => { if (tab === 'archives') fetchArchives(); }, [tab, fetchArchives]);
  useEffect(() => { if (tab === 'coverage') fetchCoverage(); }, [tab, fetchCoverage]);

  const totalPages = Math.ceil(total / pageSize);

  const dedupedUsers = React.useMemo(() => {
    const seen = new Map();
    for (const u of logUsers) if (!seen.has(u.user_id)) seen.set(u.user_id, u);
    return Array.from(seen.values());
  }, [logUsers]);

  const resetFilters = () => {
    setFilterCategory(''); setFilterModule(''); setFilterAction(''); setFilterUser(''); setFilterRole('');
    setFilterEmail(''); setFilterDateFrom(''); setFilterDateTo(''); setSearchText(''); setFilterTargetId('');
    setPage(1); setSelectedIds(new Set()); setSearchParams({});
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAllVisible = () => {
    const allVisible = logs.every(l => selectedIds.has(l.id));
    const next = new Set(selectedIds);
    if (allVisible) { logs.forEach(l => next.delete(l.id)); }
    else { logs.forEach(l => next.add(l.id)); }
    setSelectedIds(next);
  };

  // ─── Export ───────────────────────────────────────────────────
  const doExport = async (scope) => {
    if (!isAdmin) return;
    try {
      const body = { scope, format: 'csv' };
      if (scope === 'filtered') body.filters = currentFilters();
      if (scope === 'selected') body.selected_log_ids = Array.from(selectedIds);
      if (scope === 'selected' && selectedIds.size === 0) {
        toast.error('Select at least one log to export.');
        return;
      }
      const res = await axios.post(`${API}/logs/export`, body, { responseType: 'blob' });
      const filename = `pfaphub_audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
      downloadBlob(res.data, filename);
      toast.success(`Exported ${scope} logs`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  };

  // ─── Archive/Clear ────────────────────────────────────────────
  const submitArchive = async () => {
    if (!isAdmin) return;
    setArchiveBusy(true);
    try {
      const body = { scope: archiveScope };
      if (archiveScope === 'before_date') {
        if (!archiveBeforeDate) {
          toast.error('Pick a date to archive before.');
          setArchiveBusy(false); return;
        }
        body.before_date = new Date(archiveBeforeDate + 'T23:59:59').toISOString();
      }
      if (archiveScope === 'filtered') body.filters = currentFilters();
      if (archiveScope === 'selected') {
        if (selectedIds.size === 0) {
          toast.error('Select at least one log first.');
          setArchiveBusy(false); return;
        }
        body.selected_log_ids = Array.from(selectedIds);
      }
      const res = await axios.post(`${API}/logs/archive-clear`, body);
      toast.success(`Archived ${res.data.log_count} log(s) → ${res.data.title}`);
      setArchiveModal(false);
      setSelectedIds(new Set());
      fetchLogs();
      fetchMeta();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Archive failed');
    } finally {
      setArchiveBusy(false);
    }
  };

  const downloadArchive = async (archiveId, title) => {
    try {
      const res = await axios.get(`${API}/logs/archives/${archiveId}/download`, { responseType: 'blob' });
      downloadBlob(res.data, `${(title || archiveId).replace(/\s+/g, '_')}.csv`);
    } catch { toast.error('Download failed'); }
  };

  // ─── Render ───────────────────────────────────────────────────
  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isAdmin ? 'System Logs' : 'My Activity Logs'}
          </h2>
          <p className="text-gray-600 dark:text-gray-500 mt-1">
            {isAdmin ? 'Complete audit trail of all system actions' : 'Your personal audit trail.'}
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mb-4">
          <TabsList data-testid="syslogs-tabs">
            <TabsTrigger value="active" data-testid="tab-active">Active Logs</TabsTrigger>
            {isAdmin && <TabsTrigger value="archives" data-testid="tab-archives">Log Archives</TabsTrigger>}
            {isAdmin && <TabsTrigger value="coverage" data-testid="tab-coverage">Audit Coverage</TabsTrigger>}
          </TabsList>

          {/* ═══════════════════ ACTIVE LOGS TAB ═══════════════════ */}
          <TabsContent value="active" className="mt-4">
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <Card data-testid="stat-total">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700"><Layers className="w-5 h-5 text-gray-600 dark:text-gray-300" /></div>
                    <div><p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total_logs}</p><p className="text-xs text-gray-500">Active Logs</p></div>
                  </CardContent>
                </Card>
                {Object.entries(categoryConfig).map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  return (
                    <Card key={key} data-testid={`stat-${key}`}>
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${cfg.color}`}><Icon className="w-5 h-5" /></div>
                        <div><p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.by_category?.[key] || 0}</p><p className="text-xs text-gray-500">{cfg.label}</p></div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {filterTargetId && (
              <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20 px-3 py-2 text-sm" data-testid="target-id-filter-banner">
                <div className="text-emerald-800 dark:text-emerald-300">
                  Showing audit entries for batch <span className="font-mono text-xs">{filterTargetId}</span>
                </div>
                <Button size="sm" variant="ghost" onClick={() => { setFilterTargetId(''); setSearchParams({}); }} className="h-7 px-2 text-emerald-800 dark:text-emerald-300" data-testid="clear-target-id-filter">
                  <X className="w-3.5 h-3.5 mr-1" /> Clear filter
                </Button>
              </div>
            )}

            {/* Filters */}
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs mb-1 block">Search</Label>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                      <Input className="pl-8 h-9 text-sm" placeholder="Search description..." value={searchText} onChange={(e) => { setSearchText(e.target.value); setPage(1); }} data-testid="search-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Category</Label>
                    <Select value={filterCategory || 'all'} onValueChange={(v) => { setFilterCategory(v === 'all' ? '' : v); setPage(1); }}>
                      <SelectTrigger className="h-9 text-sm" data-testid="filter-category"><SelectValue placeholder="All" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {Object.entries(categoryConfig).map(([k, cfg]) => <SelectItem key={k} value={k}>{cfg.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Module</Label>
                    <Select value={filterModule || 'all'} onValueChange={(v) => { setFilterModule(v === 'all' ? '' : v); setPage(1); }}>
                      <SelectTrigger className="h-9 text-sm" data-testid="filter-module"><SelectValue placeholder="All Modules" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Modules</SelectItem>
                        {logModules.map(m => <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1).replace(/_/g, ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Action</Label>
                    <Select value={filterAction || 'all'} onValueChange={(v) => { setFilterAction(v === 'all' ? '' : v); setPage(1); }}>
                      <SelectTrigger className="h-9 text-sm" data-testid="filter-action"><SelectValue placeholder="All Actions" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Actions</SelectItem>
                        {logActions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Actor Role</Label>
                    <Select value={filterRole || 'all'} onValueChange={(v) => { setFilterRole(v === 'all' ? '' : v); setPage(1); }}>
                      <SelectTrigger className="h-9 text-sm" data-testid="filter-role"><SelectValue placeholder="All Roles" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Actor (user)</Label>
                    <Select value={filterUser || 'all'} onValueChange={(v) => { setFilterUser(v === 'all' ? '' : v); setPage(1); }}>
                      <SelectTrigger className="h-9 text-sm" data-testid="filter-user"><SelectValue placeholder="All Users" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        {dedupedUsers.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.email} ({u.role})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Actor email contains</Label>
                    <Input className="h-9 text-sm" placeholder="e.g. coach@" value={filterEmail} onChange={(e) => { setFilterEmail(e.target.value); setPage(1); }} data-testid="filter-email" />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Target ID</Label>
                    <Input className="h-9 text-sm" placeholder="athlete/batch id" value={filterTargetId} onChange={(e) => { setFilterTargetId(e.target.value); setPage(1); }} data-testid="filter-target-id" />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">From</Label>
                    <Input type="date" className="h-9 text-sm" value={filterDateFrom} onChange={(e) => { setFilterDateFrom(e.target.value); setPage(1); }} data-testid="filter-date-from" />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">To</Label>
                    <Input type="date" className="h-9 text-sm" value={filterDateTo} onChange={(e) => { setFilterDateTo(e.target.value); setPage(1); }} data-testid="filter-date-to" />
                  </div>
                  <div className="flex items-end">
                    <Button size="sm" variant="ghost" className="h-9 px-2 w-full" onClick={resetFilters} data-testid="clear-filters">Clear filters</Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Toolbar: Selection + Export + Archive */}
            {isAdmin && (
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2" data-testid="logs-toolbar">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedIds.size > 0 ? `${selectedIds.size} selected · ` : ''}{total} total
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => doExport('all')} data-testid="export-all-btn"><Download className="w-4 h-4 mr-1" /> Export All</Button>
                  <Button size="sm" variant="outline" onClick={() => doExport('filtered')} data-testid="export-filtered-btn"><Download className="w-4 h-4 mr-1" /> Export Filtered</Button>
                  <Button size="sm" variant="outline" onClick={() => doExport('selected')} disabled={selectedIds.size === 0} data-testid="export-selected-btn"><Download className="w-4 h-4 mr-1" /> Export Selected</Button>
                  <Button size="sm" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20" onClick={() => setArchiveModal(true)} data-testid="archive-clear-btn"><Archive className="w-4 h-4 mr-1" /> Archive / Clear</Button>
                </div>
              </div>
            )}

            {/* Log Entries */}
            <Card data-testid="logs-list">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div></div>
                ) : logs.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">No logs found matching your filters</div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {isAdmin && (
                      <div className="px-4 py-2 bg-gray-50/50 dark:bg-gray-800/30 flex items-center gap-3 text-xs">
                        <Checkbox checked={logs.length > 0 && logs.every(l => selectedIds.has(l.id))} onCheckedChange={toggleSelectAllVisible} data-testid="select-all-visible" />
                        <span className="text-gray-500">Select all on page</span>
                      </div>
                    )}
                    {logs.map((log) => {
                      const catCfg = categoryConfig[log.category] || {};
                      const CatIcon = catCfg.icon || Shield;
                      const isSel = selectedIds.has(log.id);
                      return (
                        <div key={log.id} className={`px-4 py-3 transition-colors hover:bg-gray-50/80 dark:hover:bg-gray-800/50 ${isSel ? 'bg-emerald-50/30 dark:bg-emerald-900/10' : ''}`} data-testid={`log-${log.id}`}>
                          <div className="flex items-start gap-3">
                            {isAdmin && (
                              <Checkbox className="mt-1" checked={isSel} onCheckedChange={() => toggleSelect(log.id)} data-testid={`log-select-${log.id}`} onClick={(e) => e.stopPropagation()} />
                            )}
                            <button type="button" className="flex-1 text-left focus:outline-none" onClick={() => setSelectedLog(log)} data-testid={`log-row-${log.id}`}>
                              <div className="flex items-start gap-3">
                                <div className={`p-1.5 rounded-md mt-0.5 ${catCfg.color || 'bg-gray-100'}`}><CatIcon className="w-4 h-4" /></div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-900 dark:text-gray-100">{log.description}</p>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <Badge className={`text-[10px] px-1.5 py-0 ${actionColors[log.action] || 'bg-gray-100'}`}>{log.action}</Badge>
                                    {log.module && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{log.module}</Badge>}
                                    {log.user_role && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{log.user_role}</Badge>}
                                    <span className="text-xs text-gray-400">{log.user_email}</span>
                                  </div>
                                </div>
                                <div className="text-xs text-gray-400 whitespace-nowrap flex items-center gap-1"><Clock className="w-3 h-3" /> {formatTime(log.timestamp)}</div>
                              </div>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">{total} total log{total !== 1 ? 's' : ''}</p>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page"><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Page {page} of {totalPages}</span>
                  <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ═══════════════════ ARCHIVES TAB ═══════════════════ */}
          {isAdmin && (
            <TabsContent value="archives" className="mt-4">
              <Card data-testid="archives-list">
                <CardContent className="p-0">
                  {archivesLoading ? (
                    <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div></div>
                  ) : archives.length === 0 ? (
                    <div className="text-center py-12 text-gray-500" data-testid="archives-empty">
                      <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      <p>No archives yet. Use <strong>Archive / Clear</strong> on the Active Logs tab to create one.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {archives.map(a => (
                        <div key={a.id} className="px-4 py-3 hover:bg-gray-50/80 dark:hover:bg-gray-800/50" data-testid={`archive-${a.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{a.title}</p>
                              <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-gray-500">
                                <Badge variant="outline" className="text-[10px]">{a.scope}</Badge>
                                <span>{a.log_count} logs</span>
                                <span>•</span>
                                <span>{a.date_from?.slice(0, 10) || '—'} → {a.date_to?.slice(0, 10) || '—'}</span>
                                <span>•</span>
                                <span>by {a.created_by_email}</span>
                                <span>•</span>
                                <span>{formatTime(a.created_at)}</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => setSelectedArchive(a)} data-testid={`archive-view-${a.id}`}>View</Button>
                              <Button size="sm" onClick={() => downloadArchive(a.id, a.title)} data-testid={`archive-download-${a.id}`}><Download className="w-3.5 h-3.5 mr-1" /> CSV</Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* ═══════════════════ COVERAGE TAB ═══════════════════ */}
          {isAdmin && (
            <TabsContent value="coverage" className="mt-4">
              {coverageLoading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div></div>
              ) : !coverage ? (
                <div className="text-center py-12 text-gray-500">Coverage unavailable.</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4" data-testid="coverage-summary">
                    {Object.entries(coverage.summary).map(([k, v]) => {
                      const meta = COVERAGE_STATUS_META[k] || {};
                      const Icon = meta.icon || Shield;
                      return (
                        <Card key={k}>
                          <CardContent className="p-4 flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${meta.color || 'bg-gray-100'}`}><Icon className="w-5 h-5" /></div>
                            <div><p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{v}</p><p className="text-xs text-gray-500">{meta.label || k}</p></div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                  {Object.entries(coverage.by_section).map(([section, items]) => (
                    <Card key={section} className="mb-4" data-testid={`coverage-section-${section}`}>
                      <CardContent className="p-0">
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{section}</h3>
                        </div>
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                          {items.map(item => {
                            const meta = COVERAGE_STATUS_META[item.status] || {};
                            const Icon = meta.icon || Shield;
                            return (
                              <div key={item.module} className="px-4 py-3 flex items-start gap-3" data-testid={`coverage-row-${item.module}`}>
                                <div className={`p-1.5 rounded-md ${meta.color}`}><Icon className="w-4 h-4" /></div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.label}</p>
                                    <Badge variant="outline" className="text-[10px]">{item.module}</Badge>
                                    <Badge className={`text-[10px] ${meta.color}`} data-testid={`coverage-status-${item.module}`}>{meta.label}</Badge>
                                  </div>
                                  {item.notes && <p className="text-xs text-gray-500 mt-1">{item.notes}</p>}
                                  {item.action_coverage?.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                      {item.action_coverage.map(ac => {
                                        const isHooked = ac.hooked !== false;
                                        const hasActivity = ac.count > 0;
                                        const cls = !isHooked
                                          ? 'border-red-300 text-red-700 dark:text-red-300'
                                          : hasActivity
                                            ? 'border-emerald-300 text-emerald-700 dark:text-emerald-300'
                                            : 'border-sky-300 text-sky-700 dark:text-sky-300';
                                        return (
                                          <Badge key={ac.action} variant="outline" className={`text-[10px] ${cls}`}>
                                            {ac.action}: {ac.count}{!isHooked ? ' ·  not hooked' : ''}
                                          </Badge>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {(item.hooked_count !== undefined && item.total_expected > 0) && (
                                    <p className="text-[11px] text-gray-500 mt-1">
                                      Hook coverage: <span className="font-medium text-gray-700 dark:text-gray-300">{item.hooked_count}/{item.total_expected}</span> · Recent activity: <span className="font-medium text-gray-700 dark:text-gray-300">{item.activity_total || 0}</span>
                                    </p>
                                  )}
                                  {item.last_audit_at && (
                                    <p className="text-[11px] text-gray-400 mt-1">Last event: {formatTime(item.last_audit_at)}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* ════════ ARCHIVE / CLEAR MODAL ════════ */}
        <Dialog open={archiveModal} onOpenChange={setArchiveModal}>
          <DialogContent data-testid="archive-modal">
            <DialogHeader>
              <DialogTitle>Archive &amp; clear logs</DialogTitle>
              <DialogDescription>
                Selected logs will be exported to CSV and marked as archived. They will no longer appear in Active Logs but the original data is preserved and can be downloaded any time.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-2">
                <Label className="text-xs">Scope</Label>
                <Select value={archiveScope} onValueChange={setArchiveScope}>
                  <SelectTrigger data-testid="archive-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All active logs</SelectItem>
                    <SelectItem value="before_date">Logs before a date</SelectItem>
                    <SelectItem value="filtered">Current filtered result set</SelectItem>
                    <SelectItem value="selected">Selected logs only ({selectedIds.size})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {archiveScope === 'before_date' && (
                <div>
                  <Label className="text-xs">Archive logs strictly before</Label>
                  <Input type="date" value={archiveBeforeDate} onChange={(e) => setArchiveBeforeDate(e.target.value)} data-testid="archive-before-date" />
                </div>
              )}
              <div className="text-xs text-gray-500 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2">
                <strong>Note:</strong> Logs are never hard-deleted. They are exported to CSV, written to a downloadable archive, and hidden from Active Logs. An audit entry recording this action will also be created.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setArchiveModal(false)}>Cancel</Button>
              <Button onClick={submitArchive} disabled={archiveBusy} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="archive-confirm-btn">
                <Archive className="w-4 h-4 mr-1" /> {archiveBusy ? 'Archiving…' : 'Archive & clear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ════════ ARCHIVE DETAIL DIALOG ════════ */}
        <Dialog open={!!selectedArchive} onOpenChange={(open) => { if (!open) setSelectedArchive(null); }}>
          <DialogContent className="max-w-2xl" data-testid="archive-detail-dialog">
            {selectedArchive && (
              <>
                <DialogHeader>
                  <DialogTitle className="truncate">{selectedArchive.title}</DialogTitle>
                  <DialogDescription>Archive {selectedArchive.id}</DialogDescription>
                </DialogHeader>
                <div className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div><p className="text-xs text-gray-500">Scope</p><p className="font-medium">{selectedArchive.scope}</p></div>
                    <div><p className="text-xs text-gray-500">Log count</p><p className="font-medium">{selectedArchive.log_count}</p></div>
                    <div><p className="text-xs text-gray-500">From</p><p className="font-medium">{selectedArchive.date_from?.slice(0, 19) || '—'}</p></div>
                    <div><p className="text-xs text-gray-500">To</p><p className="font-medium">{selectedArchive.date_to?.slice(0, 19) || '—'}</p></div>
                    <div><p className="text-xs text-gray-500">Created by</p><p className="font-medium">{selectedArchive.created_by_email}</p></div>
                    <div><p className="text-xs text-gray-500">Created at</p><p className="font-medium">{formatTimeFull(selectedArchive.created_at)}</p></div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSelectedArchive(null)}>Close</Button>
                  <Button onClick={() => downloadArchive(selectedArchive.id, selectedArchive.title)}><Download className="w-4 h-4 mr-1" /> Download CSV</Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* ════════ LOG DETAIL DIALOG ════════ */}
        <Dialog open={!!selectedLog} onOpenChange={(open) => { if (!open) setSelectedLog(null); }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="log-detail-dialog">
            {selectedLog && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Badge className={`${actionColors[selectedLog.action] || 'bg-gray-100'}`}>{selectedLog.action}</Badge>
                    <span className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{selectedLog.target_label || 'Log Entry'}</span>
                  </DialogTitle>
                  <DialogDescription className="text-sm">{selectedLog.description}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                  <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <div className="flex items-start gap-2">
                      <UserIcon className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div><p className="text-xs text-gray-500">Performed by</p><p className="font-medium text-gray-900 dark:text-gray-100">{selectedLog.user_email || '—'}</p>{selectedLog.user_role && <p className="text-xs text-gray-500">{selectedLog.user_role}</p>}</div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div><p className="text-xs text-gray-500">When</p><p className="font-medium text-gray-900 dark:text-gray-100">{formatTimeFull(selectedLog.timestamp)}</p></div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Layers className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div><p className="text-xs text-gray-500">Module</p><p className="font-medium text-gray-900 dark:text-gray-100">{selectedLog.module || '—'}</p></div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Shield className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div><p className="text-xs text-gray-500">Category</p><p className="font-medium text-gray-900 dark:text-gray-100">{categoryConfig[selectedLog.category]?.label || selectedLog.category || '—'}</p></div>
                    </div>
                    {selectedLog.ip_address && <div className="col-span-2 text-xs text-gray-500">IP: {selectedLog.ip_address}</div>}
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {selectedLog.action === 'update' ? 'Changed fields' : selectedLog.action === 'create' ? 'Created data' : selectedLog.action === 'delete' ? 'Last known state' : 'Details'}
                    </h4>
                    {Array.isArray(selectedLog.changes) && selectedLog.changes.length > 0 ? (
                      <div className="space-y-2" data-testid="log-changes-list">
                        {selectedLog.changes.map((c, i) => (
                          <div key={`${c.field || 'change'}-${i}`} className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                            <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-800 text-xs font-mono font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">{c.field}</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700">
                              <div className="p-2 bg-red-50/40 dark:bg-red-900/10"><p className="text-[10px] uppercase tracking-wide text-red-600 dark:text-red-400 mb-1">Before</p><div className="text-sm text-gray-900 dark:text-gray-100 break-words">{formatValue(c.old_value)}</div></div>
                              <div className="p-2 bg-emerald-50/40 dark:bg-emerald-900/10"><p className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400 mb-1">After</p><div className="text-sm text-gray-900 dark:text-gray-100 break-words">{formatValue(c.new_value)}</div></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : selectedLog.changes && !Array.isArray(selectedLog.changes) && typeof selectedLog.changes === 'object' ? (
                      <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-3 rounded-md overflow-x-auto max-h-64 text-gray-700 dark:text-gray-300">{JSON.stringify(selectedLog.changes, null, 2)}</pre>
                    ) : (
                      <p className="text-sm text-gray-500 italic px-3 py-4 bg-gray-50 dark:bg-gray-800/50 rounded-md text-center" data-testid="no-changes-fallback">No field-level changes recorded for this entry.</p>
                    )}
                  </div>
                  <details className="text-xs">
                    <summary className="cursor-pointer text-gray-500 hover:text-gray-700">Technical details</summary>
                    <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded font-mono text-[11px] text-gray-600 dark:text-gray-400 space-y-0.5">
                      <div>log_id: {selectedLog.id}</div>
                      {selectedLog.target_id && <div>target_id: {selectedLog.target_id}</div>}
                      {selectedLog.target_type && <div>target_type: {selectedLog.target_type}</div>}
                      <div>user_id: {selectedLog.user_id}</div>
                      {selectedLog.archived && <div className="text-orange-500">archived: true · archive_id: {selectedLog.archive_id}</div>}
                    </div>
                  </details>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SystemLogs;
